import type { ReactNode } from 'react';
import { Logo } from '@/components/Logo';
import { AuthShowcase } from './AuthShowcase';

/**
 * The frame for the signed-out screens.
 *
 * FULL BLEED. Two halves that reach every edge — the form left, the product
 * right — with no card, no page margin and no rounded corners. It was a
 * floating panel first; on a wide monitor that left a grey border framing a
 * screen that has nothing to be framed against, and made the one page a
 * prospect sees look like a dialog rather than the product.
 *
 * The form still sits in a `max-w-md` column, centred in its half. Full bleed
 * means the LAYOUT reaches the edges, not the text: a label-to-input line
 * stretched across 900px is unreadable, and the measure matters more than the
 * fill.
 *
 * RESPONSIVE, and the breakpoint is a decision rather than a default. The
 * showcase disappears below lg instead of stacking above the form: on a phone
 * it would be several hundred pixels of decoration standing between somebody
 * and the first field, and a signup form nobody reaches is worth less than no
 * artwork at all.
 *
 * `min-w-0` on the form column is defensive rather than a fix for anything
 * observed — a grid item defaults to `min-width: auto` and refuses to shrink
 * below its content, which is the same footgun as `min-height: auto` in a flex
 * column and just as invisible until somebody opens it on a phone.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh grid-cols-1 bg-surface lg:grid-cols-2">
      {/* --- The form ----------------------------------------------------- */}
      <div className="flex min-w-0 flex-col px-6 py-8 sm:px-10 sm:py-10 lg:px-14 xl:px-20">
        {/* Centred in its half rather than pinned to the corner: the form
            below is a centred column, and a corner logo above it read as two
            things that had not been introduced. */}
        <div className="flex justify-center">
          <Logo markClassName="h-9 w-9" wordmarkClassName="text-[18px]" />
        </div>

        <div className="flex min-w-0 flex-1 items-center py-8">
          <div className="mx-auto w-full max-w-md">
            <h1 className="text-[30px] font-semibold leading-[1.12] tracking-[-0.02em] sm:text-[34px]">
              {title}
            </h1>
            {subtitle !== undefined && (
              <p className="mt-3 text-sm leading-relaxed text-ink-secondary">{subtitle}</p>
            )}

            <div className="mt-7">{children}</div>

            {footer !== undefined && <div className="mt-5">{footer}</div>}
          </div>
        </div>
      </div>

      {/* --- What they get ------------------------------------------------ */}
      <AuthShowcase />
    </div>
  );
}
